import React, { Component } from "react";
import { connect } from "react-redux";
import axios, { CancelToken } from "axios";
import _ from "lodash";
import xlsx from "xlsx";

import { Translate, I18n } from "react-redux-i18n";

import ControlRow from "../utils/components/ControlRow";
import PurchaseTariffModal from "./modals/PurchaseTariffModal";
import ConfirmationModal from "../utils/components/ConfirmationModal";
import TariffCalendar from "./purchaseTariffs/TariffCalendar";

import { setAlert } from "../../actions/alertActions";
import { searchModule, momentModule } from "../utils/Utils";

import "../../styles/utils/_loadingScreen.scss";
import "../../styles/components/chargers/PurchaseTariffs.scss";
import { ReactComponent as NoTariffsIcon } from "../../styles/images/no_tariffs.svg";
import pencil from "../../styles/images/pencil.svg";
import trashcan from "../../styles/images/trashcan.svg";
import { ReactComponent as UploadIcon } from "../../styles/images/upload.svg";

const { moment, formatDate } = momentModule;
class PurchaseTariffs extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    this.cancel = null;

    this.state = {
      loading: true,
      search: "",
      tariffs: [],
      selectedTariff: null,
      importTariff: null,
      showCalendar: true,

      modals: {
        add: false,
        edit: false,
        delete: false,
        import: false,
      },
    };

    this.fetchTariffs = this.fetchTariffs.bind(this);
    this.handlePickFile = this.handlePickFile.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleOnAddNewTariff = this.handleOnAddNewTariff.bind(this);
    this.handleOnEditTariff = this.handleOnEditTariff.bind(this);
    this.handleOnEditWeekSchedule = this.handleOnEditWeekSchedule.bind(this);
    this.handleOnConfirm = this.handleOnConfirm.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.fetchTariffs();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  fetchTariffs() {
    this.safeSetState({ loading: true }, () => {
      axios
        .get("/api/private/purchaseTariff", {
          cancelToken: new CancelToken((c) => (this.cancel = c)),
        })
        .then((res) => {
          this.safeSetState({ tariffs: res.data }, () => {
            const { tariffs, selectedTariff } = this.state;
            if (selectedTariff !== null) {
              const idx = tariffs.findIndex(
                (tariff) => tariff._id === selectedTariff._id
              );
              if (idx !== -1)
                this.safeSetState({ selectedTariff: tariffs[idx] });
            }
            this.safeSetState({ loading: false });
          });
        })
        .catch((err) =>
          this.props.setAlert(I18n.t(err.response.data.message), "danger")
        );
    });
  }

  handlePickFile(form) {
    const elements = form.elements;
    this.setModal("import", false, () => {
      const name = elements.tariffName.value;
      const tariffType = elements.tariffType.value;
      const description = elements.tariffDescription.value;
      this.safeSetState(
        { importTariff: { name, tariffType, description } },
        () => {
          document.getElementById("file-picker").click();
        }
      );
    });
  }

  handleFileChange(e) {
    let file = e.target.files?.[0];
    let reader = new FileReader();
    if (file) {
      reader.onload = (e) => {
        const workbook = xlsx.read(e.target.result, {
          type: "array",
          cellDates: true,
        });

        // verify if file is valid
        if (
          workbook.Sheets[workbook.SheetNames[0]]["EV10"]?.v !==
          "template-file-control"
        ) {
          this.props.setAlert(I18n.t("purchaseTariffs_invalidFile"), "danger");
          return;
        }
        const jsonArr = xlsx.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]]
        );

        // obj with arrays for days of the week
        const weekDays = {
          sunday: [],
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
        };

        // read values from file and populate weekDays
        for (let row of jsonArr) {
          const { hour, __EMPTY_143, ...days } = row; // destructure to remove hour obj
          const startTime = moment(hour);
          const stopTime = moment(startTime).add(15, "minutes");
          const daysArr = Object.keys(days);
          for (let weekDay of daysArr) {
            weekDays[weekDay].push({
              startTime: formatDate(startTime, "time"),
              stopTime: formatDate(stopTime, "time"),
              value: row[weekDay],
            });
          }
        }

        // build weekSchedule to send to backend
        const weekSchedule = Object.keys(weekDays).map((weekDay) => ({
          weekDay: weekDay,
          scheduleTime: _.sortBy(weekDays[weekDay], "startTime"),
        }));

        const post = { ...this.state.importTariff, weekSchedule };
        axios
          .post("/api/private/purchaseTariff", post, {
            cancelToken: new CancelToken((c) => (this.cancel = c)),
          })
          .then((res) => {
            if (res.status === 200) {
              this.props.setAlert(I18n.t("tariffs_addSuccess"), "primary");
              this.fetchTariffs();
            }
          })
          .catch((err) =>
            this.props.setAlert(I18n.t(err.response.data.message), "danger")
          );
      };
      reader.readAsArrayBuffer(file);
    } else this.safeSetState({ importTariff: null });
  }

  handleOnAddNewTariff(form) {
    const elements = form.elements;
    const name = elements.tariffName.value;
    const tariffType = elements.tariffType.value;
    const description = elements.tariffDescription.value;

    const post = { name, tariffType, description, weekSchedule: [] };
    axios
      .post("/api/private/purchaseTariff", post, {
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        if (res.status === 200) {
          this.props.setAlert(I18n.t("tariffs_addSuccess"), "primary");
          this.fetchTariffs();
          this.setModal("add", false);
        }
      })
      .catch((err) =>
        this.props.setAlert(I18n.t(err.response.data.message), "danger")
      );
  }

  handleOnEditTariff(form) {
    const elements = form.elements;
    const name = elements.tariffName.value;
    const tariffType = elements.tariffType.value;
    const description = elements.tariffDescription.value;
    const weekSchedule = this.state.selectedTariff.weekSchedule;

    const patch = {
      _id: this.state.selectedTariff._id,
      name,
      tariffType,
      description,
      weekSchedule,
    };
    axios
      .patch("/api/private/purchaseTariff", patch, {
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        if (res.status === 200) {
          this.props.setAlert(I18n.t("tariffs_editSuccess"), "primary");
          this.fetchTariffs();
          this.setModal("edit", false);
        }
      })
      .catch((err) =>
        this.props.setAlert(I18n.t(err.response.data.message), "danger")
      );
  }

  handleOnEditWeekSchedule(schedule) {
    const patch = {
      _id: this.state.selectedTariff._id,
      weekSchedule: [schedule],
    };

    axios
      .patch("/api/private/purchaseTariff/addCost", patch, {
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        if (res.status === 200) {
          this.props.setAlert(I18n.t("tariffs_editSuccess"), "primary");
          this.fetchTariffs();
          this.setModal("edit", false);
        }
      })
      .catch((err) =>
        this.props.setAlert(I18n.t(err.response.data.message), "danger")
      );
  }

  handleOnConfirm() {
    axios
      .delete("/api/private/purchaseTariff", {
        cancelToken: new CancelToken((c) => (this.cancel = c)),
        data: {
          _id: this.state.selectedTariff._id,
        },
      })
      .then((res) => {
        if (res.status === 200)
          this.safeSetState({ selectedTariff: null }, () => {
            this.props.setAlert(I18n.t("tariffs_deleteSuccess"), "primary");
            this.fetchTariffs();
            this.setModal("delete", false);
          });
      })
      .catch((err) =>
        this.props.setAlert(I18n.t(err.response.data.message), "danger")
      );
  }

  setModal(modal, bool, callback) {
    let newModals = Object.assign({}, this.state.modals);
    newModals[modal] = bool;
    this.safeSetState(
      {
        modals: newModals,
      },
      callback
    );
  }

  safeSetState(params, callback) {
    if (this._mounted) this.setState(params, callback);
  }

  render() {
    return !this.state.loading ? (
      <div id="purchaseTariffs" className="main-container">
        <div className="control-bar">
          <ControlRow
            showSearch={this.state.tariffs.length > 0}
            search={this.state.search}
            onSearchChange={(e) => searchModule.setSearch(this, e.target.value)}
            buttons={[
              {
                label: "tariffs_addTariff",
                onClick: () => this.setModal("add", true),
                disabled:
                  this.state.loading ||
                  (this.state.tariffs.length >=
                    this.props.userPackage?.tariffsLimit &&
                    this.props.isAdmin === false),
              },
            ]}
          />
        </div>
        {this.state.tariffs.length === 0 && (
          <div className="no-tariffs">
            <NoTariffsIcon />
            <p>
              <Translate value="purchaseTariffs_noTariffsText" />
            </p>
          </div>
        )}
        {this.state.tariffs.length > 0 && (
          <div className="component-container">
            <div className="table-container">
              <div className="table-header">
                <div className="row no-gutters">
                  <div className="col-12 row no-gutters titles">
                    <div className="col title">
                      <Translate value="purchaseTariffs_name" />
                    </div>
                    <div className="col title">
                      <Translate value="purchaseTariffs_type" />
                    </div>
                    <div className="col title">
                      <Translate value="purchaseTariffs_description" />
                    </div>
                    <div className="col center title">
                      {/* blank col for icon buttons */}
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-values">
                {this.state.tariffs
                  .filter((i) => searchModule.filter(this, i))
                  .map((t) => {
                    return (
                      <div key={t._id} className={"row no-gutters"}>
                        <div
                          className={`col-12 row no-gutters values ${
                            this.state.selectedTariff?._id === t._id
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            this.safeSetState({
                              selectedTariff: t,
                              showCalendar: true,
                            })
                          }
                        >
                          <div className="row-overlay" />
                          <div className="col">{t.name}</div>
                          <div className="col">
                            <Translate
                              value={
                                "tariffs_" +
                                _.camelCase(t.tariffType.replace(" ", ""))
                              }
                            />
                          </div>
                          <div className="col">{t.description}</div>
                          <div className="col center">
                            <button
                              type="button"
                              className="clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.safeSetState(
                                  { selectedTariff: t, showCalendar: false },
                                  () => this.setModal("edit", true)
                                );
                              }}
                            >
                              <img src={pencil} alt="" />
                            </button>
                            <button
                              type="button"
                              className="clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.safeSetState(
                                  { selectedTariff: t, showCalendar: false },
                                  () => this.setModal("delete", true)
                                );
                              }}
                            >
                              <img src={trashcan} alt="" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="calendar-container">
              {this.state.selectedTariff !== null && this.state.showCalendar ? (
                <TariffCalendar
                  tariff={this.state.selectedTariff}
                  onEdit={this.handleOnEditWeekSchedule}
                />
              ) : (
                <div className="no-selected-tariff">
                  <NoTariffsIcon />
                  <Translate value="purchaseTariffs_noTariffSelected" />
                </div>
              )}
            </div>
          </div>
        )}
        <input
          id="file-picker"
          type="file"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: "none" }}
          onChange={this.handleFileChange}
        />
        <button
          type="button"
          className="import-file-btn link"
          onClick={() => this.setModal("import", true)}
        >
          <Translate value="purchaseTariffs_import" />
          <UploadIcon fill="#71778a" />
        </button>

        {/* add tariff modal */}
        <PurchaseTariffModal
          mode="add"
          show={this.state.modals.add}
          onHide={() => this.setModal("add", false)}
          onSubmit={this.handleOnAddNewTariff}
        />

        {/* import (add) tariff modal */}
        <PurchaseTariffModal
          mode="import"
          show={this.state.modals.import}
          onHide={() =>
            this.safeSetState({ importTariff: null }, () =>
              this.setModal("import", false)
            )
          }
          onSubmit={this.handlePickFile}
        />

        {/* edit tariff modal */}
        <PurchaseTariffModal
          mode="edit"
          selectedTariff={this.state.selectedTariff}
          show={this.state.modals.edit}
          onHide={() => {
            this.safeSetState(
              { selectedTariff: null, showCalendar: true },
              () => this.setModal("edit", false)
            );
          }}
          onSubmit={this.handleOnEditTariff}
        />

        {/* delete tariff confirmation modal */}
        <ConfirmationModal
          show={this.state.modals.delete}
          onHide={() => {
            this.safeSetState(
              { selectedTariff: null, showCalendar: true },
              () => this.setModal("delete", false)
            );
          }}
          title="tariffs_deleteTariff"
          body="tariffs_deleteTariffText"
          button="tariffs_deleteTariff"
          onConfirm={this.handleOnConfirm}
        />
      </div>
    ) : (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="generic-loading">
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  preferences: state.account.preferences,
  accountData: state.account.accountData,
  userPackage: state.account.accountData.userPackage,
  isAdmin: state.account.isAdmin,
});

export default connect(mapStateToProps, { setAlert })(PurchaseTariffs);
